<template>
  <div class="result-list">
    <h2 class="result-list-title">
      <label for="date-picker"> {{ labels.resultsOf }}: </label>
      <a-date-picker
        v-model="date"
        :placeholder="dateFormat"
        inputReadOnly
        :disabledDate="disabledDate"
        :format="dateFormat"
        mode="date"
        :defaultValue="$moment()"
        :allowClear="false"
        htmlFor="date-picker"
        @change="onChange"
      >
        <span class="date-field">
          <font-awesome-icon :icon="['far', 'calendar-days']" class="anticon" />
          {{ date ? $moment(date).format(dateFormat) : dateFormat }}
        </span>
      </a-date-picker>
    </h2>
    <div v-if="loading" :style="{ textAlign: 'center' }">
      <a-spin :tip="labels.loadingText" :spinning="loading" />
    </div>
    <a-row
      v-else-if="!loading && !!raffles.length"
      type="flex"
      justify="center"
      :gutter="[24, 24]"
    >
      <a-col v-for="(raffle, key) in raffles" :key="key" :span="18">
        <a-card class="result-list-item">
          <a-row type="flex" justify="center">
            <a-col class="result-list-item-details" :lg="18" :md="16" :sm="24">
              <span class="result-list-item-details-logo">
                <img
                  v-if="raffle.lottery.logo"
                  :src="raffle.lottery.logo"
                  :alt="raffle.lottery.name"
                  @error="raffle.lottery.logo = null"
                />
                <a-avatar
                  v-else-if="raffle.lottery.abbreviated"
                  shape="square"
                  size="large"
                  :style="{
                    backgroundColor: '#131340 ',
                    verticalAlign: 'middle',
                  }"
                >
                  {{ raffle.lottery.abbreviated | ucword }}
                </a-avatar>
              </span>
              <span class="result-list-item-details-name">
                <router-link
                  v-if="!showDescription"
                  :to="{
                    name: 'lottery-results',
                    params: { lottery: raffle.lottery.slug },
                  }"
                >
                  <h2>{{ title(raffle.lottery.name) }}</h2>
                </router-link>
                <h2 v-else>{{ title(raffle.lottery.name) }}</h2>
                <p v-if="description(raffle.lottery.name)">
                  {{ labels.raffle }}
                  {{ description(raffle.lottery.name) | ucfirst }}
                </p>
              </span>
              <span class="result-list-item-details-date">
                <label>{{ labels.raffleDateLabel }}</label>
                <p>{{ handleDateFormat(raffle.date) }}</p>
                <p>
                  <a-icon
                    type="clock-circle"
                    :style="{ marginRight: '3px' }"
                    :title="labels.lotteryCloseTimeLabel"
                  />
                  {{ lotteryEndTime(raffle.lottery, raffle.date) }}
                </p>
              </span>
            </a-col>
            <a-col
              class="result-list-item-numbers-content"
              :lg="6"
              :md="8"
              :sm="24"
            >
              <a-row>
                <a-col
                  class="result-list-item-numbers"
                  :class="
                    raffle.pick3_prize || raffle.pick4_prize ? '' : 'no-pick'
                  "
                  :lg="6"
                  :md="8"
                  :sm="24"
                >
                  <span>{{ raffle.first_prize }}</span>
                  <span>{{ raffle.second_prize }}</span>
                  <span>{{ raffle.third_prize }}</span>
                </a-col>
              </a-row>
              <a-row>
                <a-col
                  class="result-list-item-numbers-pick"
                  :lg="24"
                  :md="24"
                  :sm="24"
                  v-if="raffle.pick3_prize || raffle.pick4_prize"
                >
                  <div v-if="raffle.pick3_prize">
                    <div class="pick-title">Pick 3:</div>
                    <span>{{ raffle.pick3_prize }}</span>
                  </div>
                  <div v-if="raffle.pick4_prize">
                    <div class="pick-title">Pick 4:</div>
                    <span>{{ raffle.pick4_prize }}</span>
                  </div>
                </a-col>
              </a-row>
            </a-col>
          </a-row>
        </a-card>
      </a-col>
    </a-row>
    <a-row v-else>
      <a-col :span="24">
        <h2 class="not-results">{{ labels.notResults }}</h2>
      </a-col>
    </a-row>

    <!-- LOTTERY DESCRIPTION -->
    <a-row
      v-if="showDescription && !!lotteryDescription"
      type="flex"
      justify="center"
      :gutter="[24, 24]"
    >
      <a-col :span="18">
        <a-divider v-if="showDescription && !!lotteryDescription" />
        <p class="description-paragraph" v-html="lotteryDescription" />
      </a-col>
    </a-row>
  </div>
</template>
<script>
import { mapActions } from "vuex";
import _ from "lodash";
import moment from "moment-timezone";
import labels from "@/utils/labels";
import { DATE_FORMAT } from "../utils/utils";
export default {
  name: "ResultList",
  props: {
    showDescription: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      date: this.$moment(),
      raffles: [],
      loading: false,
      labels: labels.results,
    };
  },
  created() {
    this.getRaffles(
      this.$moment().format(this.serverDateFormat),
      this.$route.params?.lottery
    );
  },
  computed: {
    disabledDate() {
      return (date) => {
        return date && date.valueOf() > Date.now();
      };
    },
    serverDateFormat() {
      return DATE_FORMAT.SERVER_DATE;
    },
    dateFormat() {
      return DATE_FORMAT.MOMENT_DATE;
    },
    lotteryDescription() {
      if (!this.raffles?.length) return null;
      return this.raffles[0]?.lottery?.description;
    },
  },
  methods: {
    ...mapActions("raffles", ["fetchClientRaffles"]),
    async getRaffles(date, lottery = null) {
      if (date) {
        this.loading = true;
        try {
          const response = await this.fetchClientRaffles({ date, lottery });
          this.raffles = response.data;
        } catch (error) {
          console.error(error);
        } finally {
          this.loading = false;
        }
      }
    },
    onChange(date) {
      this.date = date;
    },
    handleDateFormat(date) {
      return this.$moment(date, DATE_FORMAT.SERVER_DATE)
        .format("DD @ MMMM @ YYYY")
        ?.replace("@", "de")
        ?.replace("@", "del");
    },
    title(name) {
      let arr = name.split("-");
      return arr[0].trim();
    },
    description(name) {
      let arr = name.split("-");
      return arr.length > 1 ? arr[1].trim() : null;
    },
    lotteryEndTime(lottery, date) {
      const dateFormatted = moment.tz(
        date,
        DATE_FORMAT.SERVER_DATE,
        DATE_FORMAT.TIMEZONE
      );
      const end_day = dateFormatted.locale("en").format("dddd").toLowerCase();
      const lotteryEndTime = moment
        .tz(
          `${dateFormatted.format(DATE_FORMAT.SERVER_DATE)} ${
            lottery[`${end_day}_end_time`]
          }`,
          DATE_FORMAT.SERVER_DATE_TIME,
          lottery.timezone
        )
        .tz(DATE_FORMAT.TIMEZONE)
        .format(DATE_FORMAT.MOMENT_TIME);
      return lotteryEndTime || "--:--";
    },
  },
  watch: {
    date(date, oldDate) {
      if (!_.isEqual(date, oldDate)) {
        this.getRaffles(
          date.format(this.serverDateFormat),
          this.$route.params?.lottery
        );
      }
    },
    "$route.params.lottery": {
      handler(lottery, oldLottery) {
        if (!_.isEqual(lottery, oldLottery)) {
          this.getRaffles(
            this.date ? this.$moment().format(this.serverDateFormat) : null,
            this.$route.params?.lottery
          );
        }
      },
      immediate: true,
    },
  },
};
</script>
<style lang="scss">
@import "~@/assets/scss/variable";
.result-list {
  margin-top: 24px;
  background-color: var(--card-shop-bg);
  box-shadow: 0 0 2px var(--card-shadow);
  padding: 24px 0;
  border-radius: 8px;
  box-sizing: border-box;
  @media screen and (max-width: $screen-mobile) {
    margin-top: 0;
  }
  &-title {
    font-family: "Exo 2", sans-serif;
    color: var(--text);
    font-weight: 400;
    margin: 0;
    line-height: 1.3;
    word-break: break-word;
    margin-bottom: 1.8rem;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    label {
      margin-right: 10px;
    }
    .date-field {
      font-family: "Exo 2", sans-serif;
      color: var(--text);
      padding: 7px 40px 7px 15px;
      border-radius: 0px;
      border: 1px solid var(--text);
      .anticon {
        margin-right: 5px;
      }
    }
  }
  &-item {
    .ant-card-body {
      padding: 0;
    }
    background-color: var(--results-item-bg);
    border-color: var(--results-item-bc);
    box-shadow: 0 1px 0.125rem var(--results-item-shadow);
    padding: 0;
    border-radius: 0.45rem;
    &-numbers-content {
      background: var(--primary);
      background: var(--results-item-gradient);
      border-radius: 0 0.45rem 0.45rem 0;
    }
    &-numbers,
    &-numbers-pick {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 10px;
      border-radius: 0 0.45rem 0.45rem 0;
      height: 80px;
      width: 100%;
      @media screen and (max-width: $screen-mobile) {
        width: 100%;
        border-radius: 0 0 0.45rem 0.45rem;
      }

      .pick-title {
        color: var(--text);
        margin-bottom: 10px;
        text-align: center;
      }

      span {
        font-family: "Exo 2", sans-serif;
        font-weight: 400;
        margin: 0;
        line-height: 1.3;
        font-size: 1rem;
        text-align: center;
        color: var(--text);
        font-size: 1.2rem;
        padding: 8px;
        background-color: var(--results-item-number);
        border-radius: 0.85rem;
      }
    }
    &-numbers.no-pick {
      height: 100px;
    }
    &-details {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: row;
      gap: 10px;
      padding: 0 10px 0px 30px;
      color: var(--text);
      @media screen and (max-width: $screen-mobile) {
        flex-direction: column;
      }
      &-logo {
        width: 235px;
        padding-right: 30px;
        display: flex;
        align-items: center;
        justify-content: center;
        @media screen and (max-width: $screen-mobile) {
          width: 130px;
          height: 90px;
          padding-right: 0;
        }
        img {
          width: 100%;
        }
      }
      &-name {
        width: 410px;
        @media screen and (max-width: $screen-mobile) {
          width: 100%;
          text-align: center;
        }
        p,
        h2 {
          margin-bottom: 0;
        }
        h2 {
          color: var(--text-active);
          font-size: 1.15rem;
          @media screen and (max-width: $screen-mobile) {
            font-size: 1.5rem;
          }
        }
      }
      &-date {
        text-align: right;
        width: 100%;
        @media screen and (max-width: $screen-mobile) {
          text-align: center;
          margin-bottom: 10px;
        }
        label {
          color: var(--text-active);
        }
        p {
          margin-bottom: 0;
        }
      }
    }
  }
  .not-results {
    text-align: center;
    font-size: 0.9rem;
    font-family: "Exo 2", sans-serif;
    color: var(--text);
    font-weight: 400;
    margin: 0;
    line-height: 1.3;
    word-break: break-word;
    margin-bottom: 1.8rem;
  }
  .description-paragraph {
    width: 100%;
    * {
      color: var(--text) !important;
    }
  }
}
</style>
